/**
 * PythonCodeLinter - Helper for linting Python step code
 *
 * Runs pyflakes inside the shared Pyodide runtime to surface logical
 * problems (undefined names, unused imports, redefinitions) plus syntax
 * errors. Shares PythonCodeFormatter's lazy-install + graceful-degrade
 * structure (LazyPackageInstall): the pyflakes wheel is installed via micropip
 * on first use and cached thereafter; any failure returns an empty diagnostic
 * list so the editor never breaks because linting is unavailable. (EHQ-108)
 *
 * Step code executes in the shared `__main__` namespace (see
 * python-console.helper.ts), so the driver is defined once as a function and
 * invoked per lint pass — its imports and locals (`json`, `check`, the
 * reporter) stay function-scoped and never leak into `__main__` where they
 * could shadow a user's own free names. (EHQ-108, review follow-up)
 */

import { LazyPackageInstall } from './lazy-package-install.helper';

/** Pinned to avoid micropip resolving an unexpected build from simple PyPI. */
const PYFLAKES_VERSION = '3.2.0';

/** The single (intentional) name the driver binds in `__main__`. */
const DRIVER_FN = '__ehq_run_lint__';

export interface PyLintDiagnostic {
  /** 1-based line number. */
  line: number;
  /** 1-based column number. */
  col: number;
  message: string;
  severity: 'error' | 'warning';
}

export class PythonCodeLinter {
  private readonly python: any;
  private readonly pyflakes: LazyPackageInstall;
  private driverInstalled = false;

  constructor(python: any, micropip: any) {
    this.python = python;
    this.pyflakes = new LazyPackageInstall(micropip, `pyflakes==${PYFLAKES_VERSION}`, 'PythonCodeLinter');
  }

  /**
   * Install pyflakes and the driver eagerly so the first real lint doesn't pay
   * the install round-trip. Safe to call more than once.
   */
  async initialize(): Promise<void> {
    await this.pyflakes.install();
    this.installDriver();
  }

  /**
   * Lint Python source and return diagnostics. Returns [] for empty input
   * or when pyflakes is unavailable.
   */
  async lint(code: string): Promise<PyLintDiagnostic[]> {
    if (!code || code.trim() === '') {
      return [];
    }

    if (!(await this.pyflakes.install())) {
      return [];
    }

    if (!this.installDriver()) {
      return [];
    }

    // Call the driver function with the source as its argument. Everything the
    // driver imports/binds is function-local, so nothing leaks into __main__.
    const runLint = this.python.globals.get(DRIVER_FN);
    try {
      const resultJson = runLint(code);
      const parsed = JSON.parse(resultJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('PythonCodeLinter: lint failed', error);
      return [];
    } finally {
      // globals.get() hands back a fresh PyProxy each call; free it.
      runLint?.destroy?.();
    }
  }

  /** Define the driver function once. Returns whether it's available. */
  private installDriver(): boolean {
    if (this.driverInstalled) {
      return true;
    }
    try {
      this.python.runPython(PYTHON_LINT_DRIVER_DEF);
      this.driverInstalled = true;
    } catch (error) {
      console.warn('PythonCodeLinter: failed to install lint driver', error);
    }
    return this.driverInstalled;
  }

  /** Reset install status (for testing). */
  resetInstallation(): void {
    this.pyflakes.setInstalled(false);
    this.driverInstalled = false;
  }
}

/**
 * Defines `__ehq_run_lint__(src)` in `__main__`. The function takes the source,
 * runs pyflakes with a collecting reporter, and returns a JSON string of
 * PyLintDiagnostic. Everything else (`json`, `check`, the reporter class and
 * instance) is function-local and disappears when the call returns, so the
 * shared `__main__` namespace stays clean between lint passes. pyflakes reports
 * flakes with a 1-based lineno and a 0-based col; syntax errors carry a 1-based
 * offset.
 */
const PYTHON_LINT_DRIVER_DEF = `
def ${DRIVER_FN}(src):
    import json
    from pyflakes.api import check

    class _EhqLintReporter:
        def __init__(self):
            self.items = []

        def unexpectedError(self, filename, msg):
            self.items.append({
                'line': 1, 'col': 1,
                'message': str(msg), 'severity': 'error',
            })

        def syntaxError(self, filename, msg, lineno, offset, text):
            self.items.append({
                'line': lineno or 1,
                'col': offset or 1,
                'message': 'SyntaxError: ' + str(msg),
                'severity': 'error',
            })

        def flake(self, message):
            try:
                text = message.message % message.message_args
            except Exception:
                text = str(message)
            self.items.append({
                'line': getattr(message, 'lineno', 1) or 1,
                'col': (getattr(message, 'col', 0) or 0) + 1,
                'message': text,
                'severity': 'warning',
            })

    reporter = _EhqLintReporter()
    check(src, '<step>', reporter)
    return json.dumps(reporter.items)
`;
